"use client"

import { useState, useEffect } from "react"
import { Plus, Trash2, Edit2, Loader2, Database, Upload, FileText, ImageIcon, Eye, EyeOff, X } from "lucide-react"
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage, auth } from "@/lib/firebase"
import { toast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import MediaLibrary from "./media-library"
import { ensureUniqueSlug } from "@/lib/slugs"

export default function PyqManager() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [view, setView] = useState<"list" | "edit">("list")
  const [editing, setEditing] = useState<any | null>(null)
  
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  const refresh = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "pyq"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      toast.error("Failed to fetch PYQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const resetForm = () => {
    setEditing({
      title: "",
      slug: "",
      exam: "",
      year: new Date().getFullYear().toString(),
      description: "",
      documentUrls: [],
      portal: "all"
    });
    setView("edit");
  };

  const handleEdit = (item: any) => {
    setEditing(item);
    setView("edit");
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this PYQ entry?")) return;
    try {
      await deleteDoc(doc(db, "pyq", id));
      toast.success("PYQ Deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleVisibility = async (item: any) => {
    try {
      const newStatus = item.isVisible === false ? true : false;
      await updateDoc(doc(db, "pyq", item.id), { isVisible: newStatus });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, isVisible: newStatus } : i));
      toast.success(newStatus ? "PYQ is now live" : "PYQ hidden from public");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const save = async () => {
    if (!editing?.title || !editing?.exam) {
        toast.error("Title and Exam are required");
        return;
    }
    setSaving(true);
    try {
      // Generate or validate slug
      const finalSlug = await ensureUniqueSlug("pyq", editing.slug || editing.title, editing.id || undefined);
      
      const payload = {
        title: String(editing.title),
        slug: finalSlug,
        exam: String(editing.exam || ""),
        year: String(editing.year || ""),
        description: String(editing.description || ""),
        category: String(editing.exam || "General"),
        documentUrls: Array.isArray(editing.documentUrls) ? editing.documentUrls : [],
        isVisible: editing.isVisible !== false,
        portal: editing.portal || "all",
        updatedAt: serverTimestamp(),
      };

      if (editing.id) {
        await updateDoc(doc(db, "pyq", editing.id), payload as any);
        setItems(prev => prev.map(i => i.id === editing.id ? { ...i, ...payload } : i));
        toast.success("PYQ Updated!");
      } else {
        const docRef = await addDoc(collection(db, "pyq"), { ...payload, createdAt: serverTimestamp() } as any);
        setItems(prev => [{ id: docRef.id, ...payload }, ...prev]);
        toast.success("PYQ Published!");
      }
      setEditing(null);
      setView("list");
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLocalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const storageRef = ref(storage, `pyqs/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Add to media library record for future reuse
      await addDoc(collection(db, "media"), {
        title: editing?.title ? `${editing.title} - ${file.name}` : file.name,
        url,
        type: "document",
        folder: "documents",
        createdAt: serverTimestamp(),
      });

      const current = editing?.documentUrls || [];
      setEditing({ ...editing, documentUrls: [...current, url] });
      toast.success("Document uploaded and attached!");
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };


  if (view === "list") {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
        <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">PYQ Archive</h3>
          <button onClick={resetForm} className="bg-slate-950 text-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors flex items-center gap-2">
            <Plus size={14} /> Add Previous Year Question
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Question Paper</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center">
                    <Loader2 className="animate-spin inline-block text-[#b48c36]" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center text-slate-400 text-sm">No papers uploaded.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center shrink-0">
                          <FileText size={20} className="text-slate-400" />
                        </div>
                        <div>
                          <p className="font-serif font-bold text-slate-950 leading-none mb-1">{item.title}</p>
                          <p className="text-[10px] text-slate-300 font-mono">/{item.slug || item.id}</p>
                          <div className="flex items-center gap-2 mt-2">
                             <span className="text-[9px] font-bold text-[#b48c36] uppercase tracking-widest">{item.exam}</span>
                             <span className="text-[9px] text-slate-300">•</span>
                             <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{item.year}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-[10px] font-bold uppercase tracking-widest">
                       <span className={item.portal === 'all' ? 'text-blue-500' : 'text-purple-500'}>
                        {(!item.portal || item.portal === 'all') ? 'Universal' : item.portal.toUpperCase()}
                       </span>
                    </td>
                    <td className="px-8 py-5">
                      <button 
                        onClick={() => toggleVisibility(item)}
                        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest ${item.isVisible !== false ? 'text-emerald-500' : 'text-slate-400'}`}
                      >
                        {item.isVisible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                        {item.isVisible !== false ? 'Live' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(item)} className="p-2 hover:bg-[#b48c36]/10 text-slate-400 hover:text-[#b48c36] transition-colors rounded">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
      <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">
          {editing?.id ? "Edit Question Paper" : "Publish PYQ Entry"}
        </h3>
        <button onClick={() => setView("list")} className="text-slate-400 hover:text-slate-950 transition-colors">
          Cancel
        </button>
      </div>

      <div className="p-4 sm:p-8 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Paper Title</label>
              <input 
                type="text" 
                value={editing?.title || ""}
                onChange={(e) => setEditing({...editing, title: e.target.value})}
                required
                placeholder="e.g. TSPSC Group 1 Prelims 2023"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-serif text-lg focus:outline-none focus:border-[#b48c36]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">URL Identifier (Slug)</label>
              <input 
                type="text" 
                value={editing?.slug || ""}
                onChange={(e) => setEditing({...editing, slug: e.target.value})}
                placeholder="e.g. tspsc-group-1-prelims-2023"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-mono text-sm focus:outline-none focus:border-[#b48c36]"
              />
              <p className="text-[9px] text-slate-400 italic">SEO optimized identifier. Leave blank for auto-generation.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Exam Category</label>
                <input 
                  type="text" 
                  value={editing?.exam || ""}
                  onChange={(e) => setEditing({...editing, exam: e.target.value})}
                  required
                  placeholder="e.g. TSPSC Group 1"
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest focus:outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Year</label>
                <input 
                  type="text" 
                  value={editing?.year || ""}
                  onChange={(e) => setEditing({...editing, year: e.target.value})}
                  required
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">State Portal</label>
                <select 
                  value={editing?.portal || "all"} 
                  onChange={(e) => setEditing({...editing, portal: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest"
                >
                  <option value="all">Universal</option>
                  <option value="tspsc">TSPSC Only</option>
                  <option value="appsc">APPSC Only</option>
                </select>
            </div>
          </div>

          <div className="space-y-8">
             <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Description</label>
                <textarea 
                  value={editing?.description || ""}
                  onChange={(e) => setEditing({...editing, description: e.target.value})}
                  placeholder="Details about this question paper set..."
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-sm focus:outline-none focus:border-[#b48c36] h-32 resize-none"
                />
             </div>

             <div className="space-y-4">
                <div className="flex items-center justify-between">
                   <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Linked Documents ({editing?.documentUrls?.length || 0})</label>
                   <div className="flex items-center gap-3">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded cursor-pointer hover:bg-slate-50 transition-colors flex items-center gap-1">
                         {isUploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                         Upload New
                         <input type="file" onChange={handleLocalUpload} className="hidden" accept=".pdf,.doc,.docx" disabled={isUploading} />
                      </label>
                      <button onClick={() => setIsMediaLibraryOpen(true)} className="text-[9px] font-bold uppercase tracking-widest text-[#b48c36] hover:underline flex items-center gap-1">
                         <Database size={12} /> Library
                      </button>
                   </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar">
                   {editing?.documentUrls?.map((url: string, idx: number) => (
                      <div key={idx} className="bg-slate-50 border border-slate-100 p-3 flex items-center justify-between group">
                         <div className="flex items-center gap-3 overflow-hidden">
                            <FileText size={14} className="text-[#b48c36] shrink-0" />
                            <span className="text-[10px] font-mono truncate text-slate-500">{url}</span>
                         </div>
                         <button onClick={() => {
                            const filtered = editing.documentUrls.filter((_: any, i: number) => i !== idx);
                            setEditing({...editing, documentUrls: filtered});
                         }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 size={14} />
                         </button>
                      </div>
                   ))}
                   {(!editing?.documentUrls || editing.documentUrls.length === 0) && (
                      <div className="p-8 border-2 border-dashed border-slate-50 text-center text-[10px] text-slate-300 uppercase font-bold tracking-widest">
                         No files attached
                      </div>
                   )}
                </div>
             </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-100 flex justify-end gap-4">
          <button onClick={() => setView("list")} className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-950">
            Discard
          </button>
          <button 
            onClick={save} 
            disabled={saving}
            className="bg-slate-950 text-white px-12 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] disabled:opacity-50 flex items-center gap-3"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
            {editing?.id ? "Update Paper" : "Publish Paper"}
          </button>
        </div>
      </div>

      {/* Media Library Dialog */}
      <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-slate-950 text-white">
            <DialogTitle className="font-serif text-xl">Institution Media Library</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">Select a document from your library to attach to this PYQ.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            <MediaLibrary 
              allowSelection 
              initialTab="documents"
              onSelect={(media) => {
                const current = editing?.documentUrls || [];
                setEditing({...editing, documentUrls: [...current, media.url]});
                setIsMediaLibraryOpen(false);
              }} 
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
